import React from "react";
import { useNavigate } from "react-router-dom";
import { StoreInfo } from "../types";
import { getEffectiveFeatureFlags } from "../utils/featureFlags";

interface HeroSectionProps {
  storeInfo: StoreInfo;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ storeInfo }) => {
  const navigate = useNavigate();

  // Get feature flags
  const featureFlags = getEffectiveFeatureFlags();

  const handleShopNow = () => {
    console.log("Shop Now clicked!");
    if (featureFlags.showProducts) {
      navigate("/products");
    } else {
      // Fallback to contact if products are disabled
      navigate("/contact");
    }
  };

  const handleWatchVideos = () => {
    console.log("Watch Videos clicked!");
    if (featureFlags.showVideos) {
      navigate("/videos");
    } else {
      // Fallback to contact if videos are disabled
      navigate("/contact");
    }
  };

  const valuePropositions = [
    {
      icon: "🚚",
      title: "Free Shipping",
      description: "On orders over ₹2000",
    },
    {
      icon: "⭐",
      title: "Quality Guaranteed",
      description: "Safe & certified toys",
    },
    {
      icon: "🎁",
      title: "Gift Wrapping",
      description: "Beautiful gift packaging",
    },
  ];

  return (
    <section className="relative bg-gradient-to-br from-soft-purple-100 via-soft-pink-50 to-soft-blue-50 overflow-hidden">
      {/* Background decorations - More playful */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-300 opacity-20 rounded-full animate-float"></div>
        <div
          className="absolute top-32 right-20 w-16 h-16 bg-pink-300 opacity-20 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute bottom-20 left-1/4 w-12 h-12 bg-blue-300 opacity-20 rounded-full animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute bottom-32 right-1/3 w-24 h-24 bg-purple-300 opacity-20 rounded-full animate-float"
          style={{ animationDelay: "0.5s" }}
        ></div>
        <div className="absolute top-1/2 left-1/3 w-14 h-14 bg-green-300 opacity-20 rounded-full animate-scale-bounce"></div>
        <div className="absolute top-1/4 right-1/4 w-10 h-10 bg-orange-300 opacity-20 rounded-full animate-bounce-gentle"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="text-center">
          {/* Main heading - More playful */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent animate-slide-in-left">
              {storeInfo.name}
            </span>{" "}
            <span className="inline-block animate-bounce text-5xl md:text-7xl lg:text-8xl">
              🧸
            </span>
            <span
              className="inline-block animate-bounce-gentle text-4xl md:text-6xl lg:text-7xl"
              style={{ animationDelay: "0.2s" }}
            >
              ✨
            </span>
            <span
              className="inline-block animate-bounce text-5xl md:text-7xl lg:text-8xl"
              style={{ animationDelay: "0.4s" }}
            >
              🎈
            </span>
          </h1>

          {/* Subtitle - More playful */}
          <p className="text-xl md:text-2xl font-semibold text-gray-800 mb-8 max-w-3xl mx-auto leading-relaxed animate-slide-in-right">
            🎉 Discover magical toys that spark imagination and bring joy to
            every child! 🎉
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 relative z-10">
            {/* Shop Now button - More playful */}
            {featureFlags.showProducts && (
              <button
                onClick={handleShopNow}
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 text-white font-bold py-4 px-10 rounded-full text-lg shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-110 hover-bounce cursor-pointer relative z-20 transform"
              >
                🛒 Shop Now 🎁
              </button>
            )}

            {/* Watch Videos button - More playful */}
            {featureFlags.showVideos && (
              <button
                onClick={handleWatchVideos}
                className="bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-pink-50 text-purple-600 border-4 border-purple-400 font-bold py-4 px-10 rounded-full text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 hover-bounce cursor-pointer relative z-20 transform"
              >
                <span className="flex items-center">
                  <span className="text-2xl mr-2 animate-pulse">▶️</span>
                  Watch Videos 🎬
                </span>
              </button>
            )}

            {/* Contact button - More playful */}
            <button
              onClick={() => navigate("/contact")}
              className="bg-gradient-to-r from-yellow-200 to-orange-200 hover:from-yellow-300 hover:to-orange-300 text-gray-800 border-4 border-yellow-400 font-bold py-4 px-10 rounded-full text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 hover-bounce cursor-pointer relative z-20 transform"
            >
              📞 Contact Us
            </button>
          </div>

          {/* Value Propositions - More playful */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {valuePropositions.map((item, index) => (
              <div
                key={index}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover-scale-playful border-2 border-transparent hover:border-purple-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className="text-4xl mb-3 flex justify-center animate-bounce-gentle"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {item.icon}
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2 text-center">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-sm text-center font-medium">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom wave effect */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          className="w-full h-16 text-white"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
